var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useEffect, useRef, useState } from 'react';
import DocumentDetection from './document-detection.util';
var EDGE_DETECTION_ENABLED = true;
var SCANIC_SRC = 'https://cdn.jsdelivr.net/npm/scanic@1.6.0/dist/scanic.js';
var EDGE_DETECTION_INTERVAL_MS = 350;
var MOTION_SAMPLE_INTERVAL_MS = 150;
var MOTION_SAMPLE_WIDTH = 64;
var MOTION_SAMPLE_HEIGHT = 48;
var MOTION_SCORE_THRESHOLD = 12;
var STATE_STABILITY_THRESHOLD = 2;
var scannerPromise = null;
function nativeImport(specifier) {
    return new Function('specifier', 'return import(specifier)')(specifier);
}
export function preloadFramingScanner() {
    var _this = this;
    if (!scannerPromise) {
        scannerPromise = nativeImport(SCANIC_SRC)
            .then(function (module) { return __awaiter(_this, void 0, void 0, function () {
            var scanner;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        scanner = new module.Scanner({ detector: 'ml' });
                        return [4 /*yield*/, scanner.initialize()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, scanner];
                }
            });
        }); })
            .catch(function (err) {
            scannerPromise = null;
            throw err;
        });
    }
    return scannerPromise;
}
var ADJUSTMENT_TO_STATE = {
    'move-closer': 'move-closer',
    'move-farther': 'move-farther',
    'move-left': 'move-left',
    'move-right': 'move-right',
    'move-up': 'move-up',
    'move-down': 'move-down',
    adjust: 'adjust',
};
function useLiveFramingFeedback(_a) {
    var _this = this;
    var videoElement = _a.videoElement, guideBox = _a.guideBox, enabled = _a.enabled;
    var _b = useState('idle'), state = _b[0], setState = _b[1];
    var scannerRef = useRef(null);
    var isShakyRef = useRef(false);
    var isScanningRef = useRef(false);
    var previousMotionSampleRef = useRef(null);
    var motionCanvasRef = useRef(null);
    var detectionCanvasRef = useRef(null);
    var pendingStateRef = useRef(null);
    var pendingStateCountRef = useRef(0);
    var commitState = function (next) {
        if (pendingStateRef.current === next) {
            pendingStateCountRef.current += 1;
        }
        else {
            pendingStateRef.current = next;
            pendingStateCountRef.current = 1;
        }
        if (pendingStateCountRef.current >= STATE_STABILITY_THRESHOLD) {
            setState(next);
        }
    };
    useEffect(function () {
        if (!enabled || !EDGE_DETECTION_ENABLED) {
            setState('idle');
            return;
        }
        var cancelled = false;
        preloadFramingScanner()
            .then(function (scanner) {
            if (!cancelled) {
                scannerRef.current = scanner;
            }
        })
            .catch(function (err) {
            console.warn('Live framing feedback disabled, scanner failed to load:', err);
        });
        return function () {
            cancelled = true;
        };
    }, [enabled]);
    useEffect(function () {
        if (!enabled || !videoElement) {
            return;
        }
        var motionIntervalId = window.setInterval(function () {
            if (!videoElement.videoWidth) {
                return;
            }
            if (!motionCanvasRef.current) {
                motionCanvasRef.current = document.createElement('canvas');
                motionCanvasRef.current.width = MOTION_SAMPLE_WIDTH;
                motionCanvasRef.current.height = MOTION_SAMPLE_HEIGHT;
            }
            var ctx = motionCanvasRef.current.getContext('2d');
            if (!ctx) {
                return;
            }
            ctx.drawImage(videoElement, 0, 0, MOTION_SAMPLE_WIDTH, MOTION_SAMPLE_HEIGHT);
            var sample = ctx.getImageData(0, 0, MOTION_SAMPLE_WIDTH, MOTION_SAMPLE_HEIGHT).data;
            var motionScore = DocumentDetection.computeMotionScore(previousMotionSampleRef.current, sample);
            previousMotionSampleRef.current = sample;
            isShakyRef.current = motionScore > MOTION_SCORE_THRESHOLD;
        }, MOTION_SAMPLE_INTERVAL_MS);
        var detectionIntervalId = window.setInterval(function () { return __awaiter(_this, void 0, void 0, function () {
            var scanner, canvas, ctx, cropRegion, corners, _a, wellFramed, adjustment, err_1;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (isShakyRef.current) {
                            commitState('shaky');
                            return [2 /*return*/];
                        }
                        if (!EDGE_DETECTION_ENABLED || !guideBox) {
                            commitState('idle');
                            return [2 /*return*/];
                        }
                        scanner = scannerRef.current;
                        if (!scanner || !videoElement.videoWidth || isScanningRef.current) {
                            return [2 /*return*/];
                        }
                        if (!detectionCanvasRef.current) {
                            detectionCanvasRef.current = document.createElement('canvas');
                        }
                        canvas = detectionCanvasRef.current;
                        canvas.width = videoElement.videoWidth;
                        canvas.height = videoElement.videoHeight;
                        ctx = canvas.getContext('2d');
                        if (!ctx) {
                            return [2 /*return*/];
                        }
                        ctx.drawImage(videoElement, 0, 0);
                        cropRegion = DocumentDetection.getCropRegion(guideBox, canvas.width, canvas.height);
                        isScanningRef.current = true;
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, 4, 5]);
                        return [4 /*yield*/, DocumentDetection.findDocumentContour(scanner, canvas, cropRegion)];
                    case 2:
                        corners = _b.sent();
                        if (!corners) {
                            commitState('no-document');
                            return [2 /*return*/];
                        }
                        _a = DocumentDetection.describeFraming(corners, guideBox), wellFramed = _a.wellFramed, adjustment = _a.adjustment;
                        commitState(wellFramed ? 'well-framed' : ADJUSTMENT_TO_STATE[adjustment !== null && adjustment !== void 0 ? adjustment : 'adjust']);
                        return [3 /*break*/, 5];
                    case 3:
                        err_1 = _b.sent();
                        console.warn('Live framing detection failed:', err_1);
                        return [3 /*break*/, 5];
                    case 4:
                        isScanningRef.current = false;
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        }); }, EDGE_DETECTION_INTERVAL_MS);
        return function () {
            window.clearInterval(motionIntervalId);
            window.clearInterval(detectionIntervalId);
            previousMotionSampleRef.current = null;
            isShakyRef.current = false;
            pendingStateRef.current = null;
            pendingStateCountRef.current = 0;
            setState('idle');
        };
    }, [enabled, videoElement, guideBox === null || guideBox === void 0 ? void 0 : guideBox.x, guideBox === null || guideBox === void 0 ? void 0 : guideBox.y, guideBox === null || guideBox === void 0 ? void 0 : guideBox.width, guideBox === null || guideBox === void 0 ? void 0 : guideBox.height]);
    return state;
}
export default useLiveFramingFeedback;
